import { MathUtil } from "@base/math/MathUtil";
import { IVec2, Vec2 } from "@base/math/Vec2";
import { CurtainClothShape, CurtainConfectionBottomType, CurtainConfectionType, CurtainSuspensionType, CurtainTieBackType, DEFAULT_MAX_FIX_DIST, ICurtain2dForm, ICurtain2dTrack } from "@biz/items/curtain/CurtainDefinitions";
import { CurtainLogic, ICalculatedClothInfo, ICalculatedGuideInfo, ICalculatedRopeInfo, ISlideGuideInfo } from "@biz/items/curtain/CurtainLogic";
import { IClothData } from "@biz/logic/ClothData";
import { getChecked } from "@biz/logic/getChecked";

export class Curtain2dLogic {

  static #getClothConfectionType(curtain: ICurtain2dForm, clothIndex?: number): CurtainConfectionType {
    if (clothIndex === undefined)
      return curtain.clothGlobal.confectionType;
    const cloth = curtain.clothes[clothIndex];
    return cloth.confectionType !== undefined ? cloth.confectionType : curtain.clothGlobal.confectionType;
  }

  public static getClothConfectionType(curtain: ICurtain2dForm, clothIndex?: number): CurtainConfectionType {
    return getChecked({
      selection: Curtain2dLogic.#getClothConfectionType(curtain, clothIndex),
      validItems: CurtainLogic.getPossibleConfectionTypes(curtain.curtainType),
      emptySelection: "__selectionRequired__"
    }) || "flap"
  }

  static getClothConfectionBottomType(curtain: ICurtain2dForm, clothIndex?: number): CurtainConfectionBottomType {
    if (clothIndex === undefined)
      return curtain.clothGlobal.confectionBottomType;
    const cloth = curtain.clothes[clothIndex];
    return cloth.confectionBottomType !== undefined ? cloth.confectionBottomType : curtain.clothGlobal.confectionBottomType;
  }

  static #getClothSuspensionType(curtain: ICurtain2dForm, clothIndex?: number): CurtainSuspensionType {
    if (clothIndex === undefined)
      return curtain.clothGlobal.suspensionType;
    const cloth = curtain.clothes[clothIndex];
    return (cloth.suspensionType !== undefined ? cloth.suspensionType : curtain.clothGlobal.suspensionType);
  }

  public static getClothSuspensionType(curtain: ICurtain2dForm, clothIndex: number | undefined): CurtainSuspensionType {
    return getChecked({
      selection: Curtain2dLogic.#getClothSuspensionType(curtain, clothIndex),
      validItems: CurtainLogic.getPossibleSuspensionTypes(curtain.curtainType, Curtain2dLogic.getClothConfectionType(curtain, clothIndex) || "flap"),
      emptySelection: "__selectionRequired__"
    }) || "none"
  }

  static calcClothMaxFixDist(curtain: ICurtain2dForm, clothIndex: number | undefined): number {
    const val = (clothIndex !== undefined ? curtain.clothes[clothIndex].maxFixDist : undefined) || curtain.clothGlobal.maxFixDist;
    return val || DEFAULT_MAX_FIX_DIST;
  }

  static getTieBackType(curtain: ICurtain2dForm, clothIndex: number | undefined): CurtainTieBackType | undefined {
    const clothTieBackType = clothIndex !== undefined ? curtain.clothes[clothIndex]?.tieBackType : undefined;
    return clothTieBackType !== undefined ? clothTieBackType : curtain.clothGlobal.tieBackType;
  }

  static getTieBackCount(curtain: ICurtain2dForm, clothIndex: number): number {
    if (!Curtain2dLogic.getTieBackType(curtain, clothIndex))
      return 0;
    return curtain.clothes[clothIndex].tieBackCount;
  }

  static getTieBackLength(curtain: ICurtain2dForm, clothIndex: number): number {
    const cloth = curtain.clothes[clothIndex];
    const ret = cloth.tieBackLength === "auto" ? CurtainLogic.calcRecommendTieBackLength(Curtain2dLogic.getClothGroup(curtain, clothIndex), Curtain2dLogic.getFinalClothWidthMid(curtain, clothIndex)) : cloth.tieBackLength;
    return MathUtil.round(ret, 2);
  }

  static getCloth(curtain: ICurtain2dForm, clothIndex: number | undefined): IClothData | undefined {
    const cloth = clothIndex === undefined ? undefined : curtain.clothes[clothIndex]?.cloth;
    return cloth !== undefined ? cloth : curtain.clothGlobal.cloth;
  }

  static getClothGroup(curtain: ICurtain2dForm, clothIndex: number | undefined): string {
    return Curtain2dLogic.getCloth(curtain, clothIndex)?.articleId?.group || "";
  }

  static getClothColorId(curtain: ICurtain2dForm, clothIndex: number | undefined): string {
    const colorId = clothIndex === undefined ? undefined : curtain.clothes[clothIndex].colorId;
    return (colorId !== undefined ? colorId : curtain.clothGlobal.colorId) || "";
  }

  static getClothShape(curtain: ICurtain2dForm | undefined, clothIndex: number): CurtainClothShape {
    const cloth = curtain?.clothes[clothIndex];
    return cloth?.shape || "rect"
  }

  static getClothWidth(curtain: ICurtain2dForm | undefined, clothIndex: number): number {
    const cloth = curtain?.clothes[clothIndex];
    return cloth?.width !== undefined ? cloth.width : (curtain?.clothGlobal.width || 0);
  }

  static getClothWidth2(curtain: ICurtain2dForm | undefined, clothIndex: number): number {
    const shape = Curtain2dLogic.getClothShape(curtain, clothIndex);
    const width = Curtain2dLogic.getClothWidth(curtain, clothIndex);
    const cloth = curtain?.clothes[clothIndex];
    return shape === "rect" ? width : (cloth?.width2 || width);
  }

  static getClothHeight(curtain: ICurtain2dForm | undefined, clothIndex: number): number {
    const cloth = curtain?.clothes[clothIndex];
    return cloth?.height !== undefined ? cloth.height : (curtain?.clothGlobal.height || 0);
  }

  static getClothHeight2(curtain: ICurtain2dForm | undefined, clothIndex: number): number {
    const shape = Curtain2dLogic.getClothShape(curtain, clothIndex);
    const height = Curtain2dLogic.getClothHeight(curtain, clothIndex);
    const cloth = curtain?.clothes[clothIndex];
    return shape === "rect" ? height : (cloth?.height2 || height);
  }

  static getClothAdditionFactor(curtain: ICurtain2dForm, clothIndex: number | undefined): number {
    // if (clothIndex !== undefined) {
    //   const shape = Curtain2dLogic.getClothShape(curtain, clothIndex)
    //   if (CurtainLogic.isSlopingShape(shape))
    //     return 0;
    // }

    const clothAdditionFactor = clothIndex === undefined ? undefined : curtain.clothes[clothIndex]?.additionFactor;
    const inputAdditionFactor = clothAdditionFactor !== undefined ? clothAdditionFactor : curtain.clothGlobal.additionFactor;
    return CurtainLogic.calcClothAdditionFactor(inputAdditionFactor, Curtain2dLogic.getCloth(curtain, clothIndex)?.clothType)
  }

  static getFinalClothWidthTop(curtain: ICurtain2dForm, clothIndex: number): number {
    const clothWidth = Curtain2dLogic.getClothWidth2(curtain, clothIndex);
    const additionFactor = Curtain2dLogic.getClothAdditionFactor(curtain, clothIndex);
    return CurtainLogic.calcFinalClothWidth(clothWidth, additionFactor);
  }

  static getFinalClothWidthMid(curtain: ICurtain2dForm, clothIndex: number): number {
    const clothWidth = Curtain2dLogic.getClothWidth(curtain, clothIndex);
    const additionFactor = Curtain2dLogic.getClothAdditionFactor(curtain, clothIndex);
    return CurtainLogic.calcFinalClothWidth(clothWidth, additionFactor);
  }

  static calcGuides(curtain: ICurtain2dForm): ICalculatedGuideInfo[] {
    const guides: ICalculatedGuideInfo[] = [];
    for (const track of curtain.tracks) {
      guides.push(Curtain2dLogic.calcGuide(track));
    }
    return guides;
  }

  static calcRopes(curtain: ICurtain2dForm): ICalculatedRopeInfo[] {
    const ropes: ICalculatedRopeInfo[] = [];
    for (const rope of curtain.ropes) {
      ropes.push({
        length: rope.length,
        count: rope.count,
        startTerminal: rope.startTerminal ? "00E13000125" : "",
        endTerminal: rope.endTerminal ? "00E13000125" : "",
      });
    }
    return ropes;
  }

  static calcGuide(track: ICurtain2dTrack): ISlideGuideInfo {
    return {
      type: "slide",
      count: track.count,
      articleNumber: "00E13001129",
      length: track.length,
      angleLeft: track.startAngle || 0,
      angleRight: track.endAngle || 0,
      notchStart: track.startNotch ? "00E13001773" : undefined,
      notchEnd: track.endNotch ? "00E13001773" : undefined
    }
  }

  static calcFixCount(curtain: ICurtain2dForm, clothIndex: number): number {
    const finalClothWidth = Curtain2dLogic.getFinalClothWidthTop(curtain, clothIndex);
    const maxFixDist = Curtain2dLogic.calcClothMaxFixDist(curtain, clothIndex);
    const fixCount = CurtainLogic._calcClothFixCount(finalClothWidth, maxFixDist);
    return fixCount;
  }


  static calcClothInfos(curtain: ICurtain2dForm): ICalculatedClothInfo[] {
    const clothInfos: ICalculatedClothInfo[] = [];
    for (let iCloth = 0; iCloth < curtain.clothes.length; iCloth++) {

      const finalClothWidthMid = Curtain2dLogic.getFinalClothWidthMid(curtain, iCloth);
      const finalClothWidthTop = Curtain2dLogic.getFinalClothWidthTop(curtain, iCloth);
      const maxFixDist = Curtain2dLogic.calcClothMaxFixDist(curtain, iCloth);
      const fixCount = Curtain2dLogic.calcFixCount(curtain, iCloth);
      const fixDist = CurtainLogic._calcClothFixDist(finalClothWidthTop, CurtainLogic._calcClothFixCount(finalClothWidthTop, maxFixDist))

      const ci: ICalculatedClothInfo = {
        shape: curtain.clothes[iCloth].shape || "rect",
        count: curtain.clothes[iCloth].count,
        clothIndex: iCloth,
        trackIndex: 0,
        cloth: Curtain2dLogic.getCloth(curtain, iCloth),
        colorId: Curtain2dLogic.getClothColorId(curtain, iCloth),
        width: Curtain2dLogic.getClothWidth(curtain, iCloth),
        width2: Curtain2dLogic.getClothWidth2(curtain, iCloth),
        height: Curtain2dLogic.getClothHeight(curtain, iCloth),
        height2: Curtain2dLogic.getClothHeight2(curtain, iCloth),
        finalClothWidthMid,
        finalClothWidthTop,
        additionFactor: Curtain2dLogic.getClothAdditionFactor(curtain, iCloth),
        confectionType: Curtain2dLogic.getClothConfectionType(curtain, iCloth),
        confectionBottomType: Curtain2dLogic.getClothConfectionBottomType(curtain, iCloth),
        suspensionType: Curtain2dLogic.getClothSuspensionType(curtain, iCloth),
        maxFixDist: Curtain2dLogic.calcClothMaxFixDist(curtain, iCloth),
        tieBackType: Curtain2dLogic.getTieBackType(curtain, iCloth),
        tieBackLength: Curtain2dLogic.getTieBackLength(curtain, iCloth),
        tieBackCount: Curtain2dLogic.getTieBackCount(curtain, iCloth),
        fixPositions: CurtainLogic._calcClothFixPositions(fixCount, fixDist)
      }
      clothInfos.push(ci)
    }
    return clothInfos;
  }


  static getClothArea(curtain: ICurtain2dForm, iCloth: number): number {
    const finalClothWidth = Curtain2dLogic.getFinalClothWidthMid(curtain, iCloth);
    const clothHeight = Curtain2dLogic.getClothHeight(curtain, iCloth);
    const clothHeight2 = Curtain2dLogic.getClothHeight2(curtain, iCloth);
    return CurtainLogic.calcClothArea(finalClothWidth, clothHeight, clothHeight2);
  }

  static calcTotalNetArea(data: ICurtain2dForm): number {
    let total = 0;
    for (let iCloth = 0; iCloth < data.clothes.length; iCloth++) {
      const area = Curtain2dLogic.getClothArea(data, iCloth) * (data.clothes[iCloth].count ?? 1)
      total += area;
    }
    return total;
  }

  static calcTotalFixCount(curtain: ICurtain2dForm): number {
    let total = 0;
    for (let iCloth = 0; iCloth < curtain.clothes.length; iCloth++) {
      const clothCount = curtain.clothes[iCloth].count;
      total += Curtain2dLogic.calcFixCount(curtain, iCloth) * clothCount;
    }
    return total;
  }

  static getClothSuspensionCount(curtain: ICurtain2dForm): number {
    return (curtain.suspensionCount === "auto" || curtain.suspensionCount === undefined) ? Curtain2dLogic.calcTotalFixCount(curtain) : curtain.suspensionCount
  }

  static calcShapePoints(params: { shape: CurtainClothShape, width: number, width2: number, height: number, height2: number }): IVec2[] {
    const { shape, width, width2, height, height2 } = params;

    switch (shape) {
      case "rect": {
        return [
          Vec2.from(0, 0),
          Vec2.from(width, 0),
          Vec2.from(width, height),
          Vec2.from(0, height),
        ]
      }
      case "slopingLeft": {
        const dh = Math.sqrt(width2 * width2 - width * width)
        if (Number.isNaN(dh))
          return [];
        return [
          Vec2.from(0, 0),
          Vec2.from(width, dh),
          Vec2.from(width, dh + height2),
          Vec2.from(0, height),
        ]
      }
      case "slopingRight": {
        const dh = Math.sqrt(width2 * width2 - width * width)
        if (Number.isNaN(dh))
          return [];
        return [
          Vec2.from(0, dh),
          Vec2.from(width, 0),
          Vec2.from(width, height2),
          Vec2.from(0, dh + height),
        ]
      }
    }
    return [];
  }
}